import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { PaintSplat } from "@/components/landing/PaintSplat";
import { Magnetic } from "@/components/landing/Magnetic";
import { CaseStudyNav } from "@/pages/CaseStudyNav";
import { getCaseStudy } from "@/lib/caseStudies";
import { scrollToId } from "@/lib/scroll";

const CaseStudy = () => {
  const { slug } = useParams();
  const study = getCaseStudy(slug);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [slug]);

  const { scrollYProgress } = useScroll();

  if (!study) return <Navigate to="/" replace />;

  const next = getCaseStudy(study.next);
  const navItems = [
    { id: "cs-overview", label: "Overview" },
    { id: "cs-brief", label: "Brief" },
    ...(study.gallery.length > 0 ? [{ id: "cs-gallery", label: "Gallery" }] : []),
    { id: "cs-outcome", label: "Outcome" },
  ];

  return (
    <div className="bg-[#09090b] text-[#fafafa]" data-testid="case-study-page">
      {/* Scroll progress bar */}
      <motion.div
        aria-hidden
        style={{ scaleX: scrollYProgress }}
        className="fixed top-0 left-0 right-0 h-[3px] z-[55] origin-left bg-gradient-to-r from-[#ff6b6b] via-[#e8b84b] to-[#2dd4bf]"
        data-testid="case-progress-bar"
      />
      <CaseStudyNav items={navItems} />
      <Navbar />

      {/* Hero region with themed ambient orbs */}
      <div className="relative overflow-hidden">
        <PaintSplat color="coral" className="w-[480px] h-[480px] -top-20 -right-16" rotate={18} opacity={0.44} from={70} to={-70} />
        <PaintSplat color="orange2" className="w-[400px] h-[400px] top-40 -left-24" rotate={195} opacity={0.36} from={-60} to={60} />
        <div className="relative z-10">
      {/* Back link */}
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 pt-28 md:pt-32">
        <button
          onClick={() => scrollToId("work")}
          className="inline-flex items-center gap-2 py-3 font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#9a9a9a] hover:text-[#eaeaea] active:scale-95 transition-[color,transform] duration-300"
          data-testid="case-back-link"
        >
          <ArrowLeft size={14} /> All Work
        </button>
      </div>

      {/* Hero */}
      <header id="cs-overview" className="mx-auto max-w-[1200px] px-6 md:px-10 pt-8 md:pt-12">
        <Reveal>
          <span className="font-mono-accent text-[11px] tracking-[0.3em] uppercase text-gold">
            {study.category}
          </span>
        </Reveal>
        <Reveal delay={0.06}>
          <h1 className="font-display font-semibold uppercase tracking-tighter leading-[0.92] text-4xl sm:text-5xl lg:text-6xl mt-5 max-w-4xl">
            {study.title}
          </h1>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="text-[#c8c8cc] text-base md:text-lg leading-relaxed mt-7 max-w-2xl">
            {study.summary}
          </p>
        </Reveal>

        <Reveal delay={0.18}>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-8">
            {[
              ["Client", study.client],
              ["Year", study.year],
              ["Role", study.role],
              ["Live", study.liveLabel],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#7f7f86]">
                  {k}
                </div>
                <div className="text-sm md:text-base text-[#eaeaea] mt-2 break-words">{v}</div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.24}>
          <Magnetic>
          <a
            href={study.liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-8 inline-flex items-center gap-2 rounded-full border border-transparent bg-[#ff6b6b] text-[#0a0a0a] px-6 py-3.5 font-mono-accent text-[11px] uppercase tracking-[0.18em] font-medium hover:text-white active:scale-95 transition-[color,box-shadow,transform] duration-300 hover:shadow-[0_0_28px_rgba(255,107,107,0.45)] hover:-translate-y-0.5"
            data-testid="case-visit-live"
          >
            Visit Live Site
            <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform duration-300" />
          </a>
          </Magnetic>
        </Reveal>
      </header>

      {/* Hero image */}
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 mt-14">
        <Reveal>
          <div
            className={`overflow-hidden rounded-3xl border border-[#242424] bg-[#0a0a0c] ${
              study.portrait ? "max-w-2xl mx-auto" : "aspect-[16/10]"
            }`}
          >
            <img
              src={study.hero}
              alt={study.title}
              loading="lazy"
              decoding="async"
              className={
                study.portrait
                  ? "w-full object-contain"
                  : "w-full h-full object-cover object-top"
              }
            />
          </div>
        </Reveal>
      </div>
        </div>
      </div>

      {/* Brief / Challenge / Approach */}
      <section id="cs-brief" className="mx-auto max-w-[1200px] px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
              The Brief
            </h2>
          </Reveal>
          <Reveal delay={0.06}>
            <p className="text-[#a9a9ae] leading-relaxed mt-6">{study.brief}</p>
          </Reveal>
        </div>
        <div className="md:col-span-7 space-y-12">
          <div>
            <Reveal>
              <h3 className="font-mono-accent text-[11px] uppercase tracking-[0.22em] text-gold">
                The Challenge
              </h3>
            </Reveal>
            <Reveal delay={0.06}>
              <p className="text-[#c8c8cc] text-lg md:text-xl leading-relaxed mt-4">
                {study.challenge}
              </p>
            </Reveal>
          </div>
          <div>
            <Reveal>
              <h3 className="font-mono-accent text-[11px] uppercase tracking-[0.22em] text-gold">
                Our Approach
              </h3>
            </Reveal>
            <ul className="mt-6 space-y-4">
              {study.approach.map((a, i) => (
                <Reveal key={i} delay={0.05 * i}>
                  <li className="flex gap-4 border-b border-white/10 pb-4">
                    <span className="font-mono-accent text-[11px] text-teal pt-1">
                      0{i + 1}
                    </span>
                    <span className="text-[#d4d4d8] leading-relaxed">{a}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {study.gallery.length > 0 && (
        <section id="cs-gallery" className="mx-auto max-w-[1200px] px-6 md:px-10 pb-24 md:pb-32">
          <div className="grid sm:grid-cols-2 gap-5">
            {study.gallery.map((img, i) => {
              const portrait = img.includes("megamenu");
              return (
                <Reveal key={i} delay={0.06 * i} className={portrait ? "sm:col-span-2" : ""}>
                  <div
                    className={`overflow-hidden rounded-2xl border border-[#242424] bg-[#0a0a0c] ${
                      portrait ? "flex justify-center py-6" : "aspect-[4/3]"
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${study.title} — view ${i + 1}`}
                      loading="lazy"
                      decoding="async"
                      className={
                        portrait
                          ? "max-h-[600px] w-auto object-contain"
                          : "w-full h-full object-cover object-top"
                      }
                    />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {/* Results */}
      <section id="cs-outcome" className="relative overflow-hidden bg-[#0c0c0e] border-y border-[#1c1c1c]">
        <PaintSplat color="teal2" className="w-[440px] h-[440px] -top-16 left-[15%]" rotate={-12} opacity={0.4} from={60} to={-60} />
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-20 md:py-28 relative z-10">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight">The Outcome</h2>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-6 md:gap-10 mt-12">
            {study.results.map((r, i) => (
              <Reveal key={i} delay={0.08 * i}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-7">
                  <div className="font-display text-4xl md:text-5xl tracking-tight text-teal">
                    {r.value}
                  </div>
                  <div className="text-[#a9a9ae] text-sm mt-3 leading-relaxed">{r.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="mx-auto max-w-[1000px] px-6 md:px-10 py-24 md:py-32 text-center">
        <Reveal>
          <p className="font-display text-2xl md:text-4xl leading-[1.25] tracking-tight text-[#fafafa]">
            &ldquo;{study.testimonial.quote}&rdquo;
          </p>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="font-mono-accent text-[11px] uppercase tracking-[0.22em] text-[#8b8b8b] mt-8">
            — {study.testimonial.author}
          </p>
        </Reveal>
      </section>

      {/* Next project + CTA */}
      <section className="border-t border-[#1c1c1c]">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-20 flex flex-col md:flex-row md:items-center justify-between gap-10">
          {next && (
            <Link
              to={`/work/${next.slug}`}
              className="group block -my-2 py-2 active:scale-[0.98] transition-transform duration-200"
              data-testid="case-next-project"
            >
              <div className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#7f7f86]">
                Next Project
              </div>
              <div className="font-display text-2xl md:text-3xl tracking-tight mt-2 inline-flex items-center gap-3 group-hover:text-[#ff6b6b] transition-colors duration-300">
                {next.title}
                <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform duration-300" />
              </div>
            </Link>
          )}
          <Magnetic>
          <button
            onClick={() => scrollToId("contact")}
            className="inline-flex items-center gap-2 rounded-full border border-white/20 text-[#fafafa] px-6 py-3.5 font-mono-accent text-[11px] uppercase tracking-[0.18em] hover:border-[#fbbf24] hover:text-[#fbbf24] hover:bg-[#fbbf24]/10 active:scale-95 transition-[color,background-color,border-color,transform] duration-300 self-start md:self-auto"
            data-testid="case-start-project"
          >
            Start a Project
            <ArrowUpRight size={15} />
          </button>
          </Magnetic>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudy;
